import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Eye, History, Layers, Pencil, Save, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Membership, Slide, WorkspaceConfig } from "@shared/types";
import { expandSlides } from "@shared/playlist";
import { slugify } from "@shared/slug";
import { api, ApiError, HistoryEntry, SharedGroupRef, WorkspaceSummary } from "@/lib/api";
import { SharedGroupSummary } from "@shared/types";
import { findIncompleteSlides, makeGroupRefSlide } from "@/lib/slide-factory";
import { slidesAddedSince } from "@/lib/slide-list-ops";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SettingsPanel } from "@/components/admin/SettingsPanel";
import { MembersPanel } from "@/components/admin/MembersPanel";
import { PreviewOverlay } from "@/components/admin/PreviewOverlay";
import { SlideListEditor, workspaceUploader } from "@/components/admin/SlideListEditor";

function formatSavedAt(ms: number): string {
  const d = new Date(ms);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function WorkspaceEditorPage() {
  const { slug = "" } = useParams();
  const navigate = useNavigate();

  const [workspace, setWorkspace] = useState<WorkspaceSummary | null>(null);
  const [config, setConfig] = useState<WorkspaceConfig | null>(null);
  const [version, setVersion] = useState(0);
  const [members, setMembers] = useState<Membership[]>([]);
  const [sharedGroups, setSharedGroups] = useState<Record<string, SharedGroupRef>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [available, setAvailable] = useState<SharedGroupSummary[]>([]);

  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameSlug, setRenameSlug] = useState("");

  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [loadingVersion, setLoadingVersion] = useState<number | null>(null);
  const [restoredNote, setRestoredNote] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .getWorkspace(slug)
      .then((detail) => {
        if (cancelled) return;
        setWorkspace(detail.workspace);
        setConfig(detail.config);
        setVersion(detail.version ?? 0);
        setMembers(detail.members);
        setSharedGroups(detail.sharedGroups ?? {});
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Failed to load workspace");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const updateConfig = useCallback((next: WorkspaceConfig) => {
    setConfig(next);
    setDirty(true);
  }, []);
  // Functional updates: an upload can finish minutes after it started and must
  // not overwrite Settings edits made in the meantime (and vice versa).
  const updateSlides = useCallback((slides: Slide[]) => {
    setConfig((prev) => (prev ? { ...prev, slides } : prev));
    setDirty(true);
  }, []);
  const updateSettings = useCallback((settings: WorkspaceConfig["settings"]) => {
    setConfig((prev) => (prev ? { ...prev, settings } : prev));
    setDirty(true);
  }, []);
  // Remounts the slide list (clearing its undo, selection and collapsed rows)
  // when the whole playlist is replaced from history or the server.
  const [editorKey, setEditorKey] = useState(0);
  const [conflictOpen, setConflictOpen] = useState(false);
  const [resolving, setResolving] = useState(false);

  // Always-current config, so save() can tell whether the admin kept editing
  // while the request was in flight (their newer edits must not be clobbered).
  const configRef = useRef(config);
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  // Don't let a tab close / refresh silently discard unsaved edits.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  async function openPicker() {
    setPickerOpen(true);
    try {
      setAvailable((await api.listSharedGroups()).groups);
    } catch {
      setAvailable([]);
    }
  }

  async function addSharedGroup(summary: SharedGroupSummary) {
    if (!config) return;
    setPickerOpen(false);
    try {
      const { group } = await api.getSharedGroup(summary.id);
      setSharedGroups((m) => ({ ...m, [group.id]: { id: group.id, name: group.name, slides: group.slides } }));
      const ref = makeGroupRefSlide(group.id, group.name);
      updateSlides([...(configRef.current?.slides ?? []), ref]);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to add shared group");
    }
  }

  async function openHistory() {
    setHistoryOpen(true);
    setHistory(null);
    setHistoryError(null);
    try {
      setHistory((await api.getHistory(slug)).entries);
    } catch (err) {
      setHistoryError(err instanceof ApiError ? err.message : "Failed to load save history");
    }
  }

  async function loadVersion(entry: HistoryEntry) {
    if (
      dirty &&
      !window.confirm("You have unsaved changes. Replace them with this older version?")
    ) {
      return;
    }
    setLoadingVersion(entry.version);
    try {
      const saved = await api.getHistoryVersion(slug, entry.version);
      // Linked shared groups that this editor hasn't loaded yet (e.g. removed
      // since) — fetch what we can so the list and preview can show them.
      const missing = [
        ...new Set(
          saved.config.slides
            .filter((s) => s.type === "groupref")
            .map((s) => (s as { sharedGroupId: string }).sharedGroupId)
            .filter((id) => !sharedGroups[id]),
        ),
      ];
      const groups = await Promise.all(
        missing.map((id) => api.getSharedGroup(id).then((r) => r.group).catch(() => null)),
      );
      setSharedGroups((m) => {
        const next = { ...m };
        for (const g of groups) if (g) next[g.id] = { id: g.id, name: g.name, slides: g.slides };
        return next;
      });
      // Loaded as unsaved edits on top of the current server version, so Save
      // still goes through the normal conflict check.
      updateConfig(saved.config);
      setEditorKey((k) => k + 1);
      setRestoredNote(
        `Loaded the version from ${formatSavedAt(saved.savedAt)}. Review it, then Save to make it live.`,
      );
      setHistoryOpen(false);
    } catch (err) {
      setHistoryError(err instanceof ApiError ? err.message : "Failed to load that version");
    } finally {
      setLoadingVersion(null);
    }
  }

  async function rename() {
    const name = renameValue.trim();
    if (!name) return;
    try {
      const { workspace: w } = await api.renameWorkspace(slug, name, renameSlug || undefined);
      setRenameOpen(false);
      if (w.slug !== slug) {
        // The kiosk URL changed — navigate to the new editor route.
        navigate(`/admin/screensaver/${w.slug}`, { replace: true });
      } else {
        setWorkspace((prev) => (prev ? { ...prev, name: w.name } : prev));
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to rename workspace");
    }
  }

  async function save() {
    if (!config) return;

    // The server silently drops slides that fail normalization (e.g. a YouTube
    // slide without a link). Refuse to save instead of losing the admin's work.
    const incomplete = findIncompleteSlides(config.slides, config.settings.defaultDurationSec);
    if (incomplete.length > 0) {
      setError(
        `Not saved — ${incomplete.length === 1 ? "one slide is" : `${incomplete.length} slides are`} ` +
          `incomplete and would be deleted: ${incomplete.join(", ")}. Fill in the missing info or remove ` +
          `the slide, then save again.`,
      );
      return;
    }

    setSaving(true);
    setError(null);
    const snapshot = config;
    try {
      const res = await api.saveConfig(slug, snapshot, version);
      setVersion(res.version);
      setRestoredNote(null);
      if (configRef.current === snapshot) {
        setConfig(res.config);
        setDirty(false);
      }
      // else: they kept editing while the save was in flight — keep the newer
      // local state (still marked Unsaved) instead of reverting to the response.
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setConflictOpen(true);
      else setError(err instanceof ApiError ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  // Someone else saved (often a GitHub picture import) after this editor
  // loaded. Keep the local edits and bring in slides that were ADDED on the
  // server since then — found by comparing against the snapshot this editor
  // started from — so nothing is lost on either side.
  async function keepMineAndMerge() {
    setResolving(true);
    try {
      const latest = await api.getWorkspace(slug);
      const base = await api.getHistoryVersion(slug, version).catch(() => null);
      const incoming = slidesAddedSince(
        configRef.current?.slides ?? [],
        latest.config.slides,
        base ? base.config.slides : null,
      );
      setConfig((prev) => (prev ? { ...prev, slides: [...prev.slides, ...incoming] } : prev));
      setSharedGroups((m) => ({ ...m, ...(latest.sharedGroups ?? {}) }));
      setVersion(latest.version ?? 0);
      setDirty(true);
      setConflictOpen(false);
      setError(null);
      setRestoredNote(
        incoming.length
          ? `Kept your changes and added ${incoming.length} new slide${incoming.length === 1 ? "" : "s"} ` +
              `saved elsewhere (for example a picture import) at the end of the list. Review, then Save.`
          : "Kept your changes on top of the latest version. Review, then Save.",
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load the latest version");
    } finally {
      setResolving(false);
    }
  }

  async function discardMineAndReload() {
    setResolving(true);
    try {
      const latest = await api.getWorkspace(slug);
      setConfig(latest.config);
      setVersion(latest.version ?? 0);
      setSharedGroups(latest.sharedGroups ?? {});
      setMembers(latest.members);
      setDirty(false);
      setRestoredNote(null);
      setError(null);
      setEditorKey((k) => k + 1);
      setConflictOpen(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load the latest version");
    } finally {
      setResolving(false);
    }
  }

  async function deleteWorkspace() {
    try {
      await api.deleteWorkspace(slug);
      navigate("/admin/screensaver");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to delete workspace");
      setConfirmDelete(false);
    }
  }

  if (loading) return <div className="container py-12 text-muted-foreground">Loading…</div>;
  if (error && !config)
    return (
      <div className="container py-12">
        <p className="text-destructive">{error}</p>
        <Button asChild variant="link" className="px-0">
          <Link to="/admin/screensaver">Back to workspaces</Link>
        </Button>
      </div>
    );
  if (!workspace || !config) return null;

  const isOwner = workspace.role === "owner";

  // Expand shared-group references for the preview (the kiosk expands server-side).
  const groupSlides = Object.fromEntries(
    Object.entries(sharedGroups).map(([id, g]) => [id, g.slides]),
  ) as Record<string, Slide[]>;
  const previewConfig: WorkspaceConfig = {
    ...config,
    slides: expandSlides(config.slides, groupSlides),
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      <header className="sticky top-0 z-20 border-b bg-background/95 backdrop-blur">
        <div className="container flex h-16 items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="icon" aria-label="Back">
              <Link
                to="/admin/screensaver"
                onClick={(e) => {
                  if (dirty && !window.confirm("You have unsaved changes. Leave without saving?")) {
                    e.preventDefault();
                  }
                }}
              >
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div>
              <h1 className="flex items-center gap-2 font-semibold">
                {workspace.name}
                <button
                  type="button"
                  aria-label="Rename workspace"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    setRenameValue(workspace.name);
                    setRenameSlug(workspace.slug);
                    setRenameOpen(true);
                  }}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                {dirty && <Badge variant="secondary">Unsaved</Badge>}
              </h1>
              <p className="text-xs text-muted-foreground">/screensaver/{workspace.slug}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => void openHistory()}>
              <History className="mr-1 h-4 w-4" /> History
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPreview(true)}>
              <Eye className="mr-1 h-4 w-4" /> Preview
            </Button>
            <Button asChild variant="outline" size="sm">
              <a href={`/screensaver/${workspace.slug}`} target="_blank" rel="noreferrer">
                <ExternalLink className="mr-1 h-4 w-4" /> Kiosk
              </a>
            </Button>
            <Button size="sm" onClick={() => void save()} disabled={!dirty || saving}>
              <Save className="mr-1 h-4 w-4" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </header>

      <main className="container py-6">
        {error && <p className="mb-4 text-sm text-destructive">{error}</p>}
        {restoredNote && (
          <p role="status" className="mb-4 rounded-lg border bg-muted/30 p-3 text-sm">
            {restoredNote}
          </p>
        )}
        <Tabs defaultValue="slides">
          <TabsList>
            <TabsTrigger value="slides">Slides ({config.slides.length})</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
            <TabsTrigger value="members">Members ({members.length})</TabsTrigger>
          </TabsList>

          {/* Kept mounted while another tab is open, so undo, selection and
              collapsed rows survive a quick trip to Settings. */}
          <TabsContent value="slides" forceMount className="space-y-4 data-[state=inactive]:hidden">
            <div className="rounded-lg border bg-muted/30 p-4 text-sm">
              <p className="mb-2 font-medium">Fit modes — how an image/video fills the screen</p>
              <ul className="grid gap-1.5 text-muted-foreground sm:grid-cols-3">
                <li>
                  <span className="font-medium text-foreground">Contain</span> — shows the whole
                  image; may add black bars. Safe default, nothing is cut off.
                </li>
                <li>
                  <span className="font-medium text-foreground">Cover</span> — fills the screen;
                  crops whatever spills past the edges.
                </li>
                <li>
                  <span className="font-medium text-foreground">Fill</span> — stretches to fit the
                  screen exactly; may distort the image.
                </li>
              </ul>
              <p className="mt-3 text-xs text-muted-foreground">
                Tip: give slides the same <strong>Group</strong> name to keep them together and in
                order when Shuffle is on. Use <strong>Add shared group</strong> to reuse a group
                across workspaces.
              </p>
            </div>

            <SlideListEditor
              key={editorKey}
              slides={config.slides}
              onSlidesChange={updateSlides}
              uploadMedia={workspaceUploader(slug)}
              defaultDuration={config.settings.defaultDurationSec}
              sharedGroups={sharedGroups}
              textDefaults={
                config.settings.branding.useBrandTextColors
                  ? {
                      bgColor: config.settings.branding.brandBgColor,
                      textColor: config.settings.branding.brandTextColor,
                    }
                  : undefined
              }
              toolbarExtra={
                <Button variant="outline" onClick={() => void openPicker()}>
                  <Layers className="mr-1 h-4 w-4" /> Add shared group
                </Button>
              }
            />
          </TabsContent>

          <TabsContent value="settings" className="space-y-4">
            <SettingsPanel
              settings={config.settings}
              onChange={updateSettings}
              uploadMedia={workspaceUploader(slug)}
            />
            {isOwner && (
              <div className="rounded-lg border border-destructive/40 p-4">
                <h3 className="font-medium text-destructive">Danger zone</h3>
                <p className="mb-3 text-sm text-muted-foreground">
                  Deleting a workspace removes its playlist and revokes all access.
                </p>
                <Button variant="destructive" size="sm" onClick={() => setConfirmDelete(true)}>
                  <Trash2 className="mr-1 h-4 w-4" /> Delete workspace
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="members">
            <MembersPanel
              slug={slug}
              members={members}
              canManage={isOwner}
              onMembersChange={setMembers}
            />
          </TabsContent>
        </Tabs>
      </main>

      {preview && <PreviewOverlay config={previewConfig} onClose={() => setPreview(false)} />}

      <Dialog open={conflictOpen} onOpenChange={(o) => !resolving && setConflictOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>This playlist changed while you were editing</DialogTitle>
            <DialogDescription>
              Someone else saved it, or a picture was imported from GitHub. Choose how to continue —
              nothing is saved until you press Save.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" disabled={resolving} onClick={() => void discardMineAndReload()}>
              Discard my changes and load theirs
            </Button>
            <Button disabled={resolving} onClick={() => void keepMineAndMerge()}>
              {resolving ? "Loading…" : "Keep my changes and add their new slides"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add a shared group</DialogTitle>
            <DialogDescription>
              Pick a reusable group to link into this workspace. Editing the group later updates it
              here automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-80 space-y-2 overflow-y-auto py-2">
            {available.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No shared groups yet.{" "}
                <Link to="/admin/shared-groups" className="underline">
                  Create one
                </Link>
                .
              </p>
            ) : (
              available.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => void addSharedGroup(g)}
                  className="flex w-full items-center justify-between rounded-md border p-3 text-left hover:bg-accent"
                >
                  <span className="font-medium">{g.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {g.slideCount} slide{g.slideCount === 1 ? "" : "s"}
                  </span>
                </button>
              ))
            )}
          </div>
          <DialogFooter>
            <Button asChild variant="outline">
              <Link to="/admin/shared-groups">Manage shared groups</Link>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save history</DialogTitle>
            <DialogDescription>
              The last 30 saves of this playlist. Loading one puts it in the editor as unsaved
              changes — nothing changes on the screens until you Save.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-96 space-y-2 overflow-y-auto py-2">
            {historyError && <p className="text-sm text-destructive">{historyError}</p>}
            {history === null && !historyError && (
              <p className="text-sm text-muted-foreground">Loading…</p>
            )}
            {history?.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No saves recorded yet. History starts with the next Save.
              </p>
            )}
            {history?.map((entry, i) => (
              <div
                key={entry.version}
                className="flex items-center justify-between gap-3 rounded-md border p-3"
              >
                <div className="min-w-0 text-sm">
                  <p className="font-medium">
                    {formatSavedAt(entry.savedAt)}
                    {i === 0 && (
                      <Badge variant="secondary" className="ml-2">
                        Live now
                      </Badge>
                    )}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {entry.savedBy} · {entry.slideCount} slide{entry.slideCount === 1 ? "" : "s"}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={loadingVersion !== null}
                  onClick={() => void loadVersion(entry)}
                >
                  {loadingVersion === entry.version ? "Loading…" : "Load this version"}
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void rename();
            }}
          >
            <DialogHeader>
              <DialogTitle>Rename workspace</DialogTitle>
              <DialogDescription>
                Changing the URL updates the kiosk address — remember to point the screen / .scr at
                the new link.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={renameValue}
                  onChange={(e) => {
                    setRenameValue(e.target.value);
                    setRenameSlug(slugify(e.target.value));
                  }}
                  autoFocus
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Kiosk URL</Label>
                <Input value={renameSlug} onChange={(e) => setRenameSlug(slugify(e.target.value))} />
                <p className="text-xs text-muted-foreground">
                  New kiosk URL: /screensaver/{renameSlug || "…"}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRenameOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this workspace?</DialogTitle>
            <DialogDescription>
              This permanently deletes “{workspace.name}” and its playlist. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => void deleteWorkspace()}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
